import PropTypes from 'prop-types';
import { css } from '@emotion/react';

import { TRACK_LABELS } from 'lib/tracking';

import { colors, fontSize } from 'styles/theme';
import { setOpacity } from 'styles/utils';

import useDisableScroll from 'hooks/useDisableScroll';

import CloseIcon from '../icons/CloseIcon';
import Container from '../Container/Container';
import Flexbox from '../Flexbox/Flexbox';
import Link from '../Link/Link';
import Text from '../Text/Text';
import CtaButton from '../CtaButton/CtaButton';

import { headerHeightMobileSm } from './Navbar.config';
import NavbarLogo from './NavbarLogo';

const sidebarCss = isOpen => css`
  display: block;
  background-color: ${colors.white};
  color: ${colors.textColor};
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  min-height: 100vh;
  transform: translateX(${isOpen ? 0 : 100}%);
  transition: transform 400ms ease;
`;

const sidebarHeaderCss = css`
  border-bottom: solid 1px ${setOpacity(colors.textColor, 0.2)};
  height: ${headerHeightMobileSm}px;
  padding-left: 15px;
`;

const containerCss = css`
  /** A fix for iOS gestures and Android browsers. */
  height: 80vh;
`;

const menuContentCss = css`
  height: 100%;
`;

const buttonCss = css`
  padding: 14px;
  line-height: 0;
  background-color: transparent;
  border: none;
  outline: none;
`;

const iconCss = css`
  width: 1.5em;
  height: 1.5em;
`;

const linksWrapper = css`
  padding-top: 34px;
`;

const linkCss = css`
  display: block;
  color: ${colors.textColor};
  padding: 13px 0;
`;

/**
 * @param {object} props
 */
function Sidebar({ currentSlug, isOpen, onMenuClose, links }) {
  useDisableScroll(isOpen);

  return (
    <div css={sidebarCss(isOpen)}>
      <Flexbox
        css={sidebarHeaderCss}
        justifyContent="space-between"
        alignItems="center"
      >
        <NavbarLogo isMobileLogoIcon={true} />
        <button css={buttonCss} onClick={onMenuClose}>
          <CloseIcon css={iconCss} />
        </button>
      </Flexbox>

      <Container css={containerCss}>
        {/* Link */}
        <Flexbox
          css={menuContentCss}
          direction="column"
          justifyContent="space-between"
        >
          <div css={linksWrapper}>
            {links.map(({ href, label, lowPriority }) => (
              <Link css={linkCss} key={`${label}-${href}`} href={href}>
                <Text
                  isMui={false}
                  fontSize={lowPriority ? fontSize.sm : fontSize.xl}
                  fontWeight={currentSlug === href ? 500 : 400}
                >
                  {label}
                </Text>
              </Link>
            ))}
          </div>

          {/* Get your kit button */}
          <Flexbox justifyContent="center">
            <CtaButton trackLabel={TRACK_LABELS.MOBILE_MENU} fullWidth />
          </Flexbox>
        </Flexbox>
      </Container>
    </div>
  );
}

Sidebar.propTypes = {
  currentSlug: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onMenuClose: PropTypes.func.isRequired,
  links: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidebar;
